import { Loading, PrimaryButton, SecondaryButton, useModal } from '@shape-ds/components';
import { usePostHog } from 'posthog-js/react';
import { Classification, Status } from 'queries/assets/contract';
import { useCasesQuery } from 'queries/cases/queries';
import { CasePatchPayload, caseTabs } from 'queries/cases/types';
import {
  useInvalidateEventsQueries,
  useOptionsByEventQuery,
  useUpdateBatchEventMutation,
  useUpdateEventMutation,
} from 'queries/events';
import { useInvalidateRecordsQueries } from 'queries/events/records';
import {
  EventBatchUpdateMutationProps,
  EventItem,
  EventUpdateMutationPayload,
  EventUpdateMutationProps,
  NextStep,
} from 'queries/events/types';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSelect, FormTextField } from 'ui/FormComponents';
import { buildWhyNotUsefulOptions } from 'utils/eventsObjects';
import { useSnackbar } from '../../hooks/useSnackbar';
import { ListCases } from './ListCases';
import { useGlobalNotification } from 'hooks';

type ChangeStatusFormData = {
  not_useful_reason?: string;
  next_step?: NextStep;
  comments: string;
  classification: Classification;
  status: Status;
};

type ChartStatusType = {
  selectedRow: EventItem;
};

export const AttachToCase = ({ selectedRow }: ChartStatusType) => {
  const { t } = useTranslation();
  const { defaultSnackbar } = useSnackbar();
  const { closeAll, closeModal, openModal } = useModal();

  const invalidateRecords = useInvalidateRecordsQueries(selectedRow.id);
  const invalidateEvents = useInvalidateEventsQueries(selectedRow.id);

  const posthog = usePostHog();

  const { displaySnackbar } = useGlobalNotification();

  const { data: eventReasonsOptions, isLoading } = useOptionsByEventQuery({
    eventId: selectedRow.id,
  });
  const { data: caseList } = useCasesQuery({
    filters: { activeTab: caseTabs.open, assets_id: [selectedRow.asset.id] },
  });

  const {
    control,
    formState: { isValid, isSubmitting },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = useForm<ChangeStatusFormData>({
    mode: 'onChange',
    defaultValues: {
      classification: selectedRow.classification ?? '',
      comments: selectedRow?.comments ?? '',
      next_step: selectedRow?.next_step ?? '',
      not_useful_reason: selectedRow?.not_useful_reason ?? '',
      status: selectedRow.status,
    },
  });

  const not_useful_reason = watch('not_useful_reason');
  const classification = watch('classification');
  const next_step = watch('next_step');

  const isUseful = classification === Classification.USEFUL;

  const onRequestSuccess = (
    data?: EventUpdateMutationPayload | CasePatchPayload,
    variables?: EventUpdateMutationProps | EventBatchUpdateMutationProps,
  ) => {
    if (variables) {
      posthog?.capture('EVENT_ACKNOWLEDGED', variables);
    }

    if (next_step === NextStep.NO_ACTION || !isUseful) {
      defaultSnackbar({
        message: t('event_page:snackbarMessages.success.message'),
        type: 'success',
      });
      return closeModal();
    }
  };

  const refetchData = () => {
    invalidateEvents();
    invalidateRecords();
  };

  const onRequestError = () => {
    defaultSnackbar({
      message: t('event_page:snackbarMessages.error.message'),
      type: 'error',
    });
  };

  const mutationOptions = {
    onSuccess: onRequestSuccess,
    onError: onRequestError,
  };

  const eventUpdateMutation = useUpdateEventMutation(refetchData);
  const eventBatchUpdateMutation = useUpdateBatchEventMutation(refetchData);

  const updateEvent = (data: ChangeStatusFormData) => {
    const payload: ChangeStatusFormData = {
      classification: data.classification,
      comments: data.comments,
      status: Status.CLOSED,
      not_useful_reason: data.not_useful_reason,
      next_step: data.next_step,
    };

    if (isUseful) {
      payload['next_step'] = data.next_step;
      payload['not_useful_reason'] = undefined;
    } else {
      payload['not_useful_reason'] = data.not_useful_reason;
      payload['next_step'] = undefined;
    }

    if (selectedRow.children && selectedRow.children.length > 0) {
      eventBatchUpdateMutation.mutate(
        {
          ...payload,
          event_ids: selectedRow.children.map((child) => child.id) ?? [],
        },
        mutationOptions,
      );
    } else {
      eventUpdateMutation.mutate({ ...payload, eventId: selectedRow.id }, mutationOptions);
    }
  };

  const onSubmit = () => {
    const data = getValues();

    updateEvent(data);

    if (isUseful) {
      if (data.next_step === NextStep.NO_ACTION) {
        closeModal();
        return displaySnackbar({
          message: t('event_page:snackbarMessages.success.message'),
          type: 'success',
        });
      }

      openModal(<ListCases data={caseList} event={selectedRow} nextStep={data.next_step} />, {
        title: t('event_page:components.modalAck.labels.ATTACH_ALARM_TO_AN_EXISTING_CASE_TITLE'),
        onClose: closeAll,
      });
    }
  };

  const nextStepOptions = useMemo(() => {
    const fallbackNextSteps = [NextStep.NO_ACTION, NextStep.ATTACH, NextStep.CREATE];

    const nextStepsToUse =
      eventReasonsOptions?.next_step && Array.isArray(eventReasonsOptions.next_step)
        ? eventReasonsOptions.next_step
        : fallbackNextSteps;

    return nextStepsToUse.map((option) => ({
      value: option,
      label: t(`event_page:components.modalAck.labels.${option}`, {
        quantity: caseList?.length || 0,
      }),
      disabled: option === NextStep.ATTACH && (caseList?.length || 0) === 0,
    }));
  }, [eventReasonsOptions, t, caseList]);

  const whyNotUsefulOptions = useMemo(
    () => buildWhyNotUsefulOptions(eventReasonsOptions, not_useful_reason),
    [eventReasonsOptions, not_useful_reason],
  );

  const classificationOptions = [
    { value: 'USEFUL', label: t('event_page:chart_status.classification.USEFUL') },
    { value: 'NOT_USEFUL', label: t('event_page:chart_status.classification.NOT_USEFUL') },
  ];

  if (isLoading) return <Loading />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="columns mx-0  mb-1">
        <div className="column is-half pl-0 pr-2">
          <FormSelect
            name="classification"
            label={t('event_page:components.modalAck.labels.classification')}
            selectOptionData={classificationOptions}
            placeHolderText={t('event_page:components.modalAck.placeholders.select_an_option')}
            control={control}
            isRequired
          />
        </div>
        <div className="column is-half pl-2 pr-0">
          <span style={{ display: isUseful ? 'inline' : 'none' }}>
            <FormSelect
              name="next_step"
              label={t('event_page:components.modalAck.labels.next_step')}
              selectOptionData={nextStepOptions}
              placeHolderText={t('event_page:components.modalAck.placeholders.select_an_option')}
              control={control}
              isRequired={isUseful}
            />
          </span>
          <span style={{ display: !classification || isUseful ? 'none' : 'inline' }}>
            <FormSelect
              name="not_useful_reason"
              label={t('event_page:components.modalAck.labels.why_not_useful')}
              selectOptionData={whyNotUsefulOptions}
              placeHolderText={t('event_page:components.modalAck.placeholders.select_an_option')}
              control={control}
              isRequired={!isUseful}
            />
          </span>
        </div>
      </div>

      <FormTextField
        name="comments"
        label={t('event_page:components.modalAck.labels.comments')}
        placeHolderText={t('event_page:components.modalAck.placeholders.comments')}
        rows={3}
        control={control}
        isRequired
      />

      <div className="is-flex is-justify-content-flex-end mt-5 is-2">
        <SecondaryButton
          onClick={() => {
            reset();
            closeModal();
          }}
          className="mr-4"
        >
          {t('event_page:components.modalAck.btns.cancel')}
        </SecondaryButton>

        <PrimaryButton
          className="chart-status-save-btn"
          type="submit"
          color="primary"
          disabled={!isValid || isSubmitting}
        >
          {selectedRow.status === Status.OPEN
            ? t('event_page:components.modalAck.btns.ack')
            : t('event_page:components.modalAck.btns.confirm')}
        </PrimaryButton>
      </div>
    </form>
  );
};
